import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError, api, deviceId } from '../api.ts';
import { realNow } from '../clock.ts';
import { cx } from '../kit/cx.ts';
import { Button, CodeField, SectionHeader } from '../kit/index.ts';
import t from '../kit/type.module.css';
import s from './SignIn.module.css';

const hhmm = (ms: number) => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Sign in with an emailed code (F-12, mockup S-01). The session arrives as an HttpOnly cookie.
 *  Codes expire after 10 minutes and allow 5 attempts (FR-AUTH-08); both are shown. */
const KEY = 'pf.signin';
type Pending = { email: string; sentAt: number };

/** The code step in progress, if its code can still be valid (10 minutes). */
function pending(): Pending | null {
  try {
    const p = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Pending | null;
    return p && realNow() - p.sentAt < 600_000 ? p : null;
  } catch {
    return null;
  }
}

function remember(p: Pending | null) {
  try {
    if (p) sessionStorage.setItem(KEY, JSON.stringify(p));
    else sessionStorage.removeItem(KEY);
  } catch {}
}

export function SignIn() {
  const queries = useQueryClient();
  // Where we were, kept for this tab: a phone browser may reload the page while you fetch the code.
  const saved = pending();
  const [email, setEmail] = useState(saved?.email ?? '');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>(saved ? 'code' : 'email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<number | null>(saved?.sentAt ?? null);
  const [attempts, setAttempts] = useState(0);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const sendCode = (e?: FormEvent) => {
    e?.preventDefault();
    void run(async () => {
      await api('/auth/code', { method: 'POST', body: { email: email.trim() } });
      setCode('');
      setAttempts(0);
      const at = realNow();
      setSentAt(at);
      setStep('code');
      remember({ email: email.trim(), sentAt: at });
    });
  };

  const verify = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      try {
        await api('/auth/verify', {
          method: 'POST',
          body: {
            email: email.trim(),
            code,
            device: { id: deviceId(), name: navigator.platform || 'Browser', platform: 'web' },
          },
        });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setAttempts((n) => n + 1);
          setCode('');
          throw new ApiError(
            401,
            'unauthorized',
            attempts + 1 >= 5
              ? 'That was the last attempt for this code. Send a new one.'
              : 'That code is wrong or has expired. Check the latest email, or send a new code.',
          );
        }
        throw err;
      }
      remember(null);
      await queries.invalidateQueries({ queryKey: ['me'] });
    });
  };

  const [name, domain] = email.trim().split('@');
  const masked = name && domain ? `${name[0]}···@${domain}` : email;

  return (
    <main className={s.page}>
      <SectionHeader title="Pathfinder" variant="screen" stripe />
      {step === 'email' ? (
        <form onSubmit={sendCode} className={s.step}>
          <div>
            <h2 className={cx(t.titleL, s.flat)}>Sign in</h2>
            <p className={cx(t.body, s.muted, s.flat)}>
              We'll email you a 6-digit code. It works once and expires in 10 minutes.
            </p>
          </div>
          <label className={s.field}>
            <span className={t.srOnly}>Email</span>
            <input
              className={cx(s.input, t.body)}
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <Button
            type="submit"
            variant="primary"
            icon={ArrowRight}
            leader
            fullWidth
            disabled={busy || !email.includes('@')}
          >
            {busy ? 'Sending…' : 'Send code'}
          </Button>
        </form>
      ) : (
        <form onSubmit={verify} className={s.step}>
          <div>
            <h2 className={cx(t.titleL, s.flat)}>Enter the code</h2>
            <p className={cx(t.body, s.muted, s.flat)}>
              We sent a 6-digit code to <strong className={s.strong}>{masked}</strong>. It expires in 10
              minutes.
            </p>
          </div>
          <CodeField label="6-digit code" value={code} onChange={setCode} />
          <div className={s.status}>
            <span className={cx(t.micro, s.muted)}>
              Expires {sentAt ? hhmm(sentAt + 600_000) : '--:--'} · Attempt {Math.min(attempts + 1, 5)} / 5
            </span>
            <button type="button" className={cx(s.link, t.bodyS)} onClick={() => sendCode()}>
              Resend
            </button>
          </div>
          <Button
            type="submit"
            variant="primary"
            icon={ArrowRight}
            leader
            fullWidth
            disabled={busy || code.length !== 6}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
          <p className={cx(t.bodyS, s.muted, s.flat)}>
            Wrong address?{' '}
            <button
              type="button"
              className={cx(s.link, t.bodyS)}
              onClick={() => {
                remember(null);
                setStep('email');
              }}
            >
              Change email
            </button>
          </p>
        </form>
      )}
      {error && (
        <p role="alert" className={cx(s.error, t.bodyS)}>
          {error}
        </p>
      )}
    </main>
  );
}
